"use client";

import React from "react";
import { motion } from "framer-motion";
import { Car, Users, ArrowRight, MapPin, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { getRoutePrice, formatPrice } from "@/lib/pricing";
import { useCurrency } from "@/hooks/useCurrency";
import { CurrencySelector } from "@/components/CurrencySelector";
import { publicHref } from "@/lib/sitePages";
interface DestinationItem {
  name: string;
  slug: string;
}
const destinations: DestinationItem[] = [{
  name: "Hurghada City",
  slug: "route-hurghada-city"
}, {
  name: "El Gouna",
  slug: "route-el-gouna"
}, {
  name: "Sahl Hasheesh",
  slug: "route-sahl-hasheesh"
}, {
  name: "Makadi Bay",
  slug: "route-makadi-bay"
}, {
  name: "Soma Bay",
  slug: "route-soma-bay"
}, {
  name: "Safaga",
  slug: "route-safaga"
}, {
  name: "Marsa Alam",
  slug: "route-marsa-alam"
}, {
  name: "Luxor",
  slug: "route-luxor"
}];
const containerVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.05
    }
  }
};
const cardVariants = {
  hidden: {
    opacity: 0,
    y: 16
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      ease: [0.21, 0.47, 0.32, 0.98]
    }
  }
};
export default function Destinations() {
  const {
    currency
  } = useCurrency();
  return <section id="popular-destinations" data-nav="light" className="bg-[#E8F2F1] text-secondary-foreground py-16 md:py-20 border-b border-border">
      <div className="mx-auto max-w-[1136px] px-6">
        {/* Header Block */}
        <div className="flex flex-col items-center text-center max-w-2xl mx-auto mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm bg-card border border-border text-[#D99000] text-xs font-medium tracking-wide uppercase mb-3">
            <span className="size-1.5 rounded-full bg-[#D99000]" />
            Transparent Fixed Rates
          </div>

          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#324A6D] mb-4">Popular Transfer Destinations&nbsp;</h2> 

          <p className="text-base text-muted-foreground flex items-center justify-center gap-2 max-w-xl leading-relaxed mb-6">
            <CheckCircle2 className="size-4 text-[#D99000] shrink-0 inline-block" />
            Every price is fixed per vehicle and includes highway tolls, terminal parking and free flight monitoring. Up to 60 minutes of free waiting time from the agreed meeting time.
          </p>

          <div className="flex flex-col items-center gap-2.5 max-w-md w-full">
            <CurrencySelector locale="en" />
            <p className="text-xs text-muted-foreground text-center">
              Prices in EGP by default; EUR and USD are approximate conversions confirmed when booking.
            </p>
          </div>
        </div>

        {/* 4x2 Grid of Destination Route Cards */}
        <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mb-12">
          {destinations.map((dest, i) => {
          const routeData = getRoutePrice(dest.slug);
          const carPrice = routeData ? formatPrice(routeData.carEgp, currency, "en") : "—";
          const vanPrice = routeData ? formatPrice(routeData.vanEgp, currency, "en") : "—";
          const target = `/DestinationsPage#route-${dest.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`;
          const hrefUrl = publicHref(target);
          return <motion.div key={dest.name} data-index={i} variants={cardVariants} className="bg-white text-card-foreground rounded-md border border-[#D9E1E2] p-5 shadow-xs flex flex-col justify-between transition-[border-color,box-shadow] duration-200 hover:border-[#D99000]/50 hover:shadow-sm">
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="size-8 rounded-sm bg-secondary flex items-center justify-center text-[#D99000]">
                      <MapPin className="size-4" />
                    </div>
                    <h3 className="text-lg font-semibold text-black tracking-tight">
                      {dest.name}
                    </h3>
                  </div>

                  <div className="space-y-2.5 pt-2 border-t border-border/60">
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-1.5 text-muted-foreground">
                        <Car className="size-3.5 text-muted-foreground" />
                        Standard Car
                      </span>
                      <span className="font-bold text-[#1A1A1A]">{carPrice}</span>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-1.5 text-muted-foreground">
                        <Users className="size-3.5 text-muted-foreground" />
                        Minivan
                      </span>
                      <span className="font-bold text-[#1A1A1A]">{vanPrice}</span>
                    </div>
                  </div>
                </div>

                <div className="mt-5 pt-3 border-t border-border/40">
                  <Button asChild variant="ghost" className="w-full justify-between h-11 md:h-8 px-3 md:px-2 text-sm md:text-xs font-medium text-[#D99000] hover:text-[#D99000] hover:bg-[#D99000]/10">
                    <Link href={hrefUrl}>
                      <span>Route details</span>
                      <ArrowRight className="size-3" />
                    </Link>
                  </Button>
                </div>
              </motion.div>;
        })}
        </motion.div>

        {/* View All CTA */}
        <div className="text-center">
          <Button asChild size="lg" className="px-8 font-medium">
            <Link className="underline cursor-pointer" to="/DestinationsPage">
              <span>View All 16 Routes & Fixed Prices</span>
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}